import React, { Component } from 'react';

import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { useGetSharedFunctionality } from './api/lending/lapiHooks';
import { useQueryParam } from './hooks/useQueryParam';
import { useUserGroups } from './hooks/useUserGroups';
import { GenericError } from './Pages/FailurePages/GenericError';
import { IwQueryClientProvider } from './store/IwQueryClientProvider';
import { Toaster } from './store/IwToast';
import logger from './utils/logger';

export const AppWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <IwQueryClientProvider>
      <ImpersonationBanner />
      <ReactQueryDevtools initialIsOpen={false} />
      <Toaster />
      <ErrorBoundary>{children}</ErrorBoundary>
    </IwQueryClientProvider>
  );
};

const ImpersonationBanner = () => {
  const stateKeyParam = useQueryParam('state_key');
  const { functionality } = useGetSharedFunctionality();
  const { isStaff } = useUserGroups();

  if (!isStaff) return null;
  if (!stateKeyParam) return null;
  if (!functionality || functionality?.is_sandbox) return null;

  return (
    <div
      role="banner"
      className="m-0 bg-error/50 p-s text-center font-bold text-white"
    >
      <p>Impersonating state key: {stateKeyParam}</p>
    </div>
  );
};

export class ErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ hasError: true });
    logger.error(error.toString(), { stack: error.stack, ...info });
  }

  render() {
    if (this.state.hasError) {
      return <GenericError errorText="something went wrong." />;
    }
    return this.props.children;
  }
}
